import React, { useEffect, useRef, useState } from 'react';
import { api, formatApiError, BACKEND_URL } from '../../lib/api';
import { toast } from 'sonner';
import { GravesitePinPicker } from '../../components/GravesitePinPicker';

export function SettingsTab({ memorialId, memorial, onChange }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => { api.get(`/memorials/${memorialId}/audit`).then(({ data }) => setLogs(data.logs)); }, [memorialId]);
  return (
    <div className="space-y-5">
      <GravesiteLocationSettings memorialId={memorialId} memorial={memorial} onChange={onChange} />
      <AmbientMusicSettings memorialId={memorialId} memorial={memorial} onChange={onChange} />
      <QRAccessSettings memorialId={memorialId} memorial={memorial} onChange={onChange} />
      <FuneralHomeSettings memorialId={memorialId} memorial={memorial} onChange={onChange} />
      <div className="ml-card">
        <h2 className="font-serif text-2xl text-[hsl(var(--primary))]">Owner-controlled actions</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">Only the Memorial Owner can export or request deletion of all memorial data.</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button className="ml-btn-outline" data-testid="export-btn" onClick={() => alert('Export request received. The team will email you within 48 hours. (placeholder)')}>Request export</button>
          <button className="ml-btn-outline !border-[hsl(var(--destructive))] !text-[hsl(var(--destructive))]" data-testid="delete-request-btn" onClick={() => alert('Delete request received. A human will confirm by email. (placeholder)')}>Request deletion</button>
        </div>
      </div>
      <div className="ml-card">
        <div className="ml-overline">Audit log</div>
        <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">Recent activity</h3>
        <div className="mt-4 space-y-2 max-h-[420px] overflow-auto">
          {logs.length === 0 && <div className="text-sm text-[hsl(var(--muted-foreground))]">No activity yet.</div>}
          {logs.map((l) => (
            <div key={l.log_id} className="border-l-2 border-[hsl(var(--accent))] pl-3 text-xs">
              <div className="font-mono">{l.action}</div>
              <div className="text-[hsl(var(--muted-foreground))]">{new Date(l.created_at).toLocaleString()}{l.actor_id ? ` · ${l.actor_id}` : ''}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* FuneralHomeSettings — iter96. Optional "Services entrusted to" card on
   the public memorial: funeral home name, logo, phone, website, address.
   Toggle controlled by the memorial admins / funeral home. */
function FuneralHomeSettings({ memorialId, memorial, onChange }) {
  const [f, setF] = useState({
    funeral_home_enabled: !!memorial?.funeral_home_enabled,
    funeral_home_name: memorial?.funeral_home_name || '',
    funeral_home_phone: memorial?.funeral_home_phone || '',
    funeral_home_website: memorial?.funeral_home_website || '',
    funeral_home_address: memorial?.funeral_home_address || '',
  });
  const [logoUrl, setLogoUrl] = useState(memorial?.funeral_home_logo_url || '');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setF({
      funeral_home_enabled: !!memorial?.funeral_home_enabled,
      funeral_home_name: memorial?.funeral_home_name || '',
      funeral_home_phone: memorial?.funeral_home_phone || '',
      funeral_home_website: memorial?.funeral_home_website || '',
      funeral_home_address: memorial?.funeral_home_address || '',
    });
    setLogoUrl(memorial?.funeral_home_logo_url || '');
    setDirty(false);
  }, [memorial]);

  const set = (k, v) => {
    setF((prev) => ({ ...prev, [k]: v }));
    setDirty(true);
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('visibility', 'public');
      fd.append('caption', 'Funeral home logo');
      const { data: media } = await api.post(`/memorials/${memorialId}/media`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = `${BACKEND_URL}/api/media/${media.media_id}/file`;
      await api.patch(`/memorials/${memorialId}`, { funeral_home_logo_url: url });
      setLogoUrl(url);
      toast.success('Logo updated');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    if (f.funeral_home_enabled && !f.funeral_home_name.trim()) {
      toast.error('Add the funeral home name before turning the card on.');
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/memorials/${memorialId}`, {
        funeral_home_enabled: f.funeral_home_enabled,
        funeral_home_name: f.funeral_home_name.trim(),
        funeral_home_phone: f.funeral_home_phone.trim(),
        funeral_home_website: f.funeral_home_website.trim(),
        funeral_home_address: f.funeral_home_address.trim(),
      });
      toast.success('Funeral home card saved');
      setDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ml-card" data-testid="funeral-home-settings-card">
      <div className="ml-overline">Funeral home partnership</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
        "Services entrusted to" card
      </h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-2xl">
        Optionally credit the funeral home handling the services. The card
        appears near the bottom of the public memorial with one-tap call,
        website, and directions.
      </p>

      <label className="mt-5 flex items-center gap-2.5 text-sm text-[hsl(var(--primary))] cursor-pointer">
        <input
          type="checkbox"
          checked={f.funeral_home_enabled}
          onChange={(e) => set('funeral_home_enabled', e.target.checked)}
          data-testid="funeral-home-toggle"
        />
        Show the funeral home card on the public memorial
      </label>

      <div className="mt-5 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="ml-label">Funeral home name</label>
          <input
            className="ml-input"
            maxLength={160}
            value={f.funeral_home_name}
            onChange={(e) => set('funeral_home_name', e.target.value)}
            placeholder="e.g. Anderson & Sons Funeral Home"
            data-testid="funeral-home-name-input"
          />
        </div>
        <div>
          <label className="ml-label">Phone</label>
          <input
            className="ml-input"
            maxLength={40}
            value={f.funeral_home_phone}
            onChange={(e) => set('funeral_home_phone', e.target.value)}
            placeholder="(555) 123-4567"
            data-testid="funeral-home-phone-input"
          />
        </div>
        <div>
          <label className="ml-label">Website</label>
          <input
            className="ml-input"
            maxLength={300}
            value={f.funeral_home_website}
            onChange={(e) => set('funeral_home_website', e.target.value)}
            placeholder="www.example.com"
            data-testid="funeral-home-website-input"
          />
        </div>
        <div>
          <label className="ml-label">Address</label>
          <input
            className="ml-input"
            maxLength={240}
            value={f.funeral_home_address}
            onChange={(e) => set('funeral_home_address', e.target.value)}
            placeholder="Street, city, state — powers Directions"
            data-testid="funeral-home-address-input"
          />
        </div>
      </div>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-lg overflow-hidden bg-white border border-[hsl(var(--primary)/0.12)] flex items-center justify-center">
          {logoUrl ? (
            <img src={logoUrl} alt="Funeral home logo" className="w-full h-full object-contain p-1" data-testid="funeral-home-logo-preview" />
          ) : (
            <span className="text-[9px] text-[hsl(var(--muted-foreground))] text-center px-1">No logo yet</span>
          )}
        </div>
        <label className="ml-btn-outline !py-2 text-xs cursor-pointer">
          {uploading ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ''; }}
            data-testid="funeral-home-logo-input"
          />
        </label>
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving || !dirty}
        className="ml-btn-primary mt-6"
        data-testid="funeral-home-save"
      >
        {saving ? 'Saving…' : dirty ? 'Save funeral home card' : 'Saved'}
      </button>
    </div>
  );
}

/* AmbientMusicSettings — admin picks one library track for the public
   memorial's optional ambient player, OR uploads a custom audio file
   (also used by Life Journey + slideshow video renders). */
function AmbientMusicSettings({ memorialId, memorial, onChange }) {
  const [tracks, setTracks] = useState([]);
  const [picked, setPicked] = useState(memorial?.ambient_music_track || '');
  const [custom, setCustom] = useState(memorial?.custom_music || null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/music/tracks').then(({ data }) => setTracks(data.tracks || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const saved = memorial?.ambient_music_track || '';
    // Map backend "custom:{id}" -> UI "__custom__" so the picker shows it as selected.
    setPicked(saved.startsWith('custom:') ? '__custom__' : saved);
    setCustom(memorial?.custom_music || null);
    setDirty(false);
  }, [memorial]);

  const save = async () => {
    setSaving(true);
    try {
      // UI value "__custom__" -> persisted "custom:{memorial_id}" so the public
      // memorial player and ambient backend agree on the same key shape.
      const persisted = picked === '__custom__' ? `custom:${memorialId}` : picked;
      await api.patch(`/memorials/${memorialId}`, { ambient_music_track: persisted });
      toast.success(persisted
        ? (persisted.startsWith('custom:') ? 'Saved — your uploaded music plays on the public page.' : 'Saved — soft music will play softly on the public page.')
        : 'Saved — no ambient music will play.');
      setDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 75 * 1024 * 1024) {
      toast.error('File is over 75 MB. Please pick a smaller audio file.');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/memorials/${memorialId}/music/upload`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setCustom(data.custom_music);
      toast.success('Uploaded — visible to every admin on this memorial. Press Save to use it.');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const removeCustom = async () => {
    if (!window.confirm('Remove the custom music for this memorial?')) return;
    try {
      await api.delete(`/memorials/${memorialId}/music/custom`);
      setCustom(null);
      // If the picked value was the custom one, clear it so save is sane.
      if (picked === '__custom__') {
        setPicked('');
        setDirty(true);
      }
      toast.success('Custom music removed');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  // Build the unified track list shown in the picker: library tracks + the
  // memorial's custom upload (if any). Every admin on the memorial sees the
  // same list because `memorial.custom_music` is persisted server-side.
  const libraryTracks = tracks.filter((t) => t.kind === 'library');

  return (
    <div className="ml-card" data-testid="ambient-music-card">
      <div className="ml-overline">Ambient music</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
        Soft music for the public page
      </h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed">
        Visitors can choose to play a quiet track while they remember.
        Nothing autoplays — they press a small button in the corner.
      </p>

      <div className="mt-5">
        <label className="ml-overline">Choose music for this memorial</label>
        <select
          value={picked}
          onChange={(e) => { setPicked(e.target.value); setDirty(true); }}
          className="ml-input mt-2"
          data-testid="ambient-music-select"
        >
          <option value="">— No ambient music —</option>
          {custom && (
            <option value="__custom__" data-testid="ambient-music-option-custom">
              ★ Uploaded · {custom.filename}
            </option>
          )}
          {libraryTracks.map((t) => (
            <option key={t.track_id} value={t.track_id}>
              {t.name} · {t.mood}
            </option>
          ))}
        </select>
        <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">
          Uploaded music is shared with every admin on this memorial — they'll see your upload here too.
        </p>
      </div>

      <div className="mt-7 pt-6 border-t border-[hsl(var(--primary)/0.08)]">
        <label className="ml-overline">Upload custom music (mp3 / m4a / wav, max 75 MB)</label>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept="audio/mpeg,audio/mp3,audio/m4a,audio/x-m4a,audio/mp4,audio/wav,audio/x-wav,audio/aac"
            className="text-sm"
            onChange={(e) => upload(e.target.files?.[0])}
            data-testid="custom-music-file-input"
          />
          {uploading && (
            <span className="text-xs text-[hsl(var(--muted-foreground))]">Uploading…</span>
          )}
          {custom && (
            <button
              type="button"
              onClick={removeCustom}
              className="ml-btn-outline !py-2 text-xs"
              data-testid="custom-music-remove"
            >
              Remove custom
            </button>
          )}
        </div>
        {custom && (
          <div
            className="mt-4 text-xs text-[hsl(var(--primary)/0.65)]"
            data-testid="custom-music-status"
          >
            <div className="font-medium">{custom.filename}</div>
            <div className="opacity-70">
              {Math.round((custom.size || 0) / 1024)} KB · uploaded{' '}
              {new Date(custom.uploaded_at).toLocaleDateString()}
            </div>
            <audio
              controls
              src={`${BACKEND_URL}/api/memorials/${memorialId}/music/custom.mp3?v=${encodeURIComponent(custom?.uploaded_at || '')}`}
              className="mt-2 w-full max-w-md"
              data-testid="custom-music-preview"
            />
            <p className="mt-2 italic">
              Every admin on this memorial can see and select this track.
            </p>
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving || !dirty}
        className="ml-btn-primary mt-6"
        data-testid="ambient-music-save"
      >
        {saving ? 'Saving…' : dirty ? 'Save music selection' : 'Saved'}
      </button>
    </div>
  );
}

/* QRAccessSettings — public vs private QR. Private requires a 4-12 char
   PIN; visitors who don't have it can still request family access. */
function QRAccessSettings({ memorialId, memorial, onChange }) {
  const [mode, setMode] = useState(memorial?.qr_access_mode || 'public');
  const [pin, setPin] = useState(memorial?.qr_pin || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setMode(memorial?.qr_access_mode || 'public');
    setPin(memorial?.qr_pin || '');
  }, [memorial]);

  const save = async () => {
    if (mode === 'private' && (pin || '').length < 4) {
      toast.error('Private QR needs a PIN of at least 4 characters.');
      return;
    }
    setSaving(true);
    try {
      const body = { qr_access_mode: mode };
      if (mode === 'private') body.qr_pin = pin;
      await api.patch(`/memorials/${memorialId}`, body);
      toast.success('QR access settings saved');
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ml-card" data-testid="qr-access-card">
      <div className="ml-overline">QR access</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
        Who can scan and see the memorial?
      </h3>

      <div className="mt-5 space-y-3">
        {[
          {
            id: 'public',
            label: 'Public',
            desc: 'Anyone who scans the QR sees the full memorial. Family Vault stays private.',
          },
          {
            id: 'private',
            label: 'Private',
            desc: 'Visitors must enter a PIN to view. They can still request family access.',
          },
        ].map((opt) => (
          <label
            key={opt.id}
            className={`flex gap-3 p-4 rounded-sm border cursor-pointer transition-colors ${
              mode === opt.id
                ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/5'
                : 'border-[hsl(var(--primary)/0.12)] hover:border-[hsl(var(--primary)/0.3)]'
            }`}
            data-testid={`qr-mode-option-${opt.id}`}
          >
            <input
              type="radio"
              name="qr-mode"
              checked={mode === opt.id}
              onChange={() => setMode(opt.id)}
              className="mt-1"
            />
            <div>
              <div className="font-medium text-[hsl(var(--primary))]">{opt.label}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1 leading-relaxed">
                {opt.desc}
              </div>
            </div>
          </label>
        ))}
      </div>

      {mode === 'private' && (
        <div className="mt-5">
          <label className="ml-overline">Memorial PIN (4–12 characters)</label>
          <input
            type="text"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\s/g, '').slice(0, 12))}
            placeholder="e.g. 4827"
            className="ml-input mt-2 font-mono tracking-widest"
            data-testid="qr-pin-input"
          />
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-2">
            Share this PIN privately with family and trusted visitors only. You
            can change it any time.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="ml-btn-primary mt-6"
        data-testid="qr-access-save"
      >
        {saving ? 'Saving…' : 'Save QR access'}
      </button>
    </div>
  );
}


/* GravesiteLocationSettings — admin places exact GPS pin + section/row/plot
   so loved ones who scan the QR can locate the grave. All fields optional.
   Visibility defaults to family-only. */
function GravesiteLocationSettings({ memorialId, memorial, onChange }) {
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [form, setForm] = useState({
    cemetery_name: memorial?.cemetery_name || '',
    cemetery_address: memorial?.cemetery_address || '',
    cemetery_section: memorial?.cemetery_section || '',
    gravesite_row: memorial?.gravesite_row || '',
    gravesite_plot: memorial?.gravesite_plot || memorial?.cemetery_lot || '',
    gravesite_notes: memorial?.gravesite_notes || '',
    gravesite_lat: memorial?.gravesite_lat ?? null,
    gravesite_lng: memorial?.gravesite_lng ?? null,
    gravesite_visibility: memorial?.gravesite_visibility || 'public',
  });

  useEffect(() => {
    setForm({
      cemetery_name: memorial?.cemetery_name || '',
      cemetery_address: memorial?.cemetery_address || '',
      cemetery_section: memorial?.cemetery_section || '',
      gravesite_row: memorial?.gravesite_row || '',
      gravesite_plot: memorial?.gravesite_plot || memorial?.cemetery_lot || '',
      gravesite_notes: memorial?.gravesite_notes || '',
      gravesite_lat: memorial?.gravesite_lat ?? null,
      gravesite_lng: memorial?.gravesite_lng ?? null,
      gravesite_visibility: memorial?.gravesite_visibility || 'public',
    });
    setDirty(false);
  }, [memorial]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setDirty(true);
  };

  const setLatLng = (la, ln) => {
    setForm((f) => ({ ...f, gravesite_lat: la, gravesite_lng: ln }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/memorials/${memorialId}`, {
        cemetery_name: form.cemetery_name.trim() || null,
        cemetery_address: form.cemetery_address.trim() || null,
        cemetery_section: form.cemetery_section.trim() || null,
        gravesite_row: form.gravesite_row.trim() || null,
        gravesite_plot: form.gravesite_plot.trim() || null,
        gravesite_notes: form.gravesite_notes.trim() || null,
        gravesite_lat: form.gravesite_lat,
        gravesite_lng: form.gravesite_lng,
        gravesite_visibility: form.gravesite_visibility,
      });
      toast.success('Gravesite location saved.');
      setDirty(false);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const hasPin = form.gravesite_lat != null && form.gravesite_lng != null;

  return (
    <div className="ml-card" data-testid="gravesite-location-card">
      <div className="ml-overline">Gravesite Location</div>
      <h3 className="font-serif text-xl mt-1 text-[hsl(var(--primary))]">
        Help families find the grave after scanning the QR
      </h3>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 leading-relaxed max-w-2xl">
        Optional. Drop a pin on the map for the exact spot, or just write
        section / row / plot. Visitors will see a "Locate Gravesite" section
        and a Get Directions button on the public memorial — only as much as
        you allow below.
      </p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="ml-label">Cemetery name</label>
          <input
            className="ml-input"
            value={form.cemetery_name}
            onChange={(e) => set('cemetery_name', e.target.value)}
            placeholder="e.g. Bonaventure Cemetery"
            data-testid="grav-cemetery-name"
          />
        </div>
        <div>
          <label className="ml-label">Cemetery address</label>
          <input
            className="ml-input"
            value={form.cemetery_address}
            onChange={(e) => set('cemetery_address', e.target.value)}
            placeholder="330 Bonaventure Rd, Savannah, GA"
            data-testid="grav-cemetery-address"
          />
        </div>
        <div>
          <label className="ml-label">Section / Garden</label>
          <input
            className="ml-input"
            value={form.cemetery_section}
            onChange={(e) => set('cemetery_section', e.target.value)}
            placeholder="Garden of Peace · Section H"
            data-testid="grav-section"
          />
        </div>
        <div>
          <label className="ml-label">Row</label>
          <input
            className="ml-input"
            value={form.gravesite_row}
            onChange={(e) => set('gravesite_row', e.target.value)}
            placeholder="Row 14"
            data-testid="grav-row"
          />
        </div>
        <div>
          <label className="ml-label">Plot / Lot</label>
          <input
            className="ml-input"
            value={form.gravesite_plot}
            onChange={(e) => set('gravesite_plot', e.target.value)}
            placeholder="Plot 7"
            data-testid="grav-plot"
          />
        </div>
        <div>
          <label className="ml-label">Visibility</label>
          <select
            className="ml-input"
            value={form.gravesite_visibility}
            onChange={(e) => set('gravesite_visibility', e.target.value)}
            data-testid="grav-visibility"
          >
            <option value="public">Public — anyone who scans the QR sees the location (recommended)</option>
            <option value="family">Family only — approved family + admins</option>
            <option value="hidden">Hidden — never show on the public page</option>
          </select>
          {(form.gravesite_visibility === 'family' || form.gravesite_visibility === 'hidden') && (
            <div
              data-testid="grav-visibility-warning"
              className="mt-2 flex gap-2 items-start text-[12px] rounded-md p-3 bg-[#FBE9CC]/70 border border-[#C5A059]/40 text-[#7A5530]"
            >
              <span className="text-[14px] leading-none">⚠</span>
              <span>
                <strong>Visitors who scan the QR will NOT see the map.</strong> Choose <em>Public</em>
                {' '}so the headstone QR can guide visitors to the gravesite.
              </span>
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="ml-label">Notes for visitors</label>
          <textarea
            rows={2}
            className="ml-input"
            value={form.gravesite_notes}
            onChange={(e) => set('gravesite_notes', e.target.value)}
            placeholder="e.g. Walk past the chapel, take the second gravel path on the left."
            data-testid="grav-notes"
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => setShowPicker((s) => !s)}
            className="ml-btn-gold !py-2.5 !px-5 text-xs"
            data-testid="grav-drop-pin-btn"
          >
            {showPicker ? 'Hide map' : (hasPin ? 'Adjust pin on map' : 'Drop Pin on Map')}
          </button>
          {hasPin && (
            <span className="text-[11px] tracking-wide font-mono text-[hsl(var(--primary))]/75" data-testid="grav-coords-summary">
              {Number(form.gravesite_lat).toFixed(6)}, {Number(form.gravesite_lng).toFixed(6)}
            </span>
          )}
        </div>

        {showPicker && (
          <div className="mt-4" data-testid="grav-pin-picker-wrapper">
            <GravesitePinPicker
              lat={form.gravesite_lat}
              lng={form.gravesite_lng}
              onChange={setLatLng}
            />
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={save}
        disabled={saving || !dirty}
        className="ml-btn-primary mt-6"
        data-testid="grav-save-btn"
      >
        {saving ? 'Saving…' : (dirty ? 'Save gravesite location' : 'Saved')}
      </button>
    </div>
  );
}
